import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin, EyeOff, RotateCcw } from "lucide-react";
import { api, fmtDateShort } from "../../lib/api";

const Row = ({ r, onPin, onExclude }) => (
  <li
    data-testid={`nl-cand-${r.rid}`}
    className={`flex items-center gap-3 py-2 ${r.excluded ? "opacity-40" : ""}`}
  >
    <img src={r.poster_url} alt="" className="h-12 w-8 object-cover rounded-sm bg-neutral-100 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium leading-tight line-clamp-2">{r.title}</div>
      <div className="text-[11px] text-neutral-500">
        {fmtDateShort(r.release_date)} · <span className={r.cinema ? "text-[var(--red)]" : ""}>{r.channel}</span>
      </div>
    </div>
    {r.selected && !r.excluded && (
      <span className="text-[9px] uppercase tracking-[0.1em] bg-black text-white px-1.5 py-0.5 rounded-sm shrink-0" data-testid={`nl-cand-in-${r.rid}`}>
        Inclus
      </span>
    )}
    <button
      type="button"
      onClick={() => onPin(r)}
      disabled={r.excluded}
      aria-label={r.pinned ? "Désépingler" : "Épingler"}
      title={r.pinned ? "Désépingler" : "Épingler (toujours inclus)"}
      data-testid={`nl-cand-pin-${r.rid}`}
      className={`p-1.5 rounded-sm border transition-colors ${r.pinned ? "bg-[var(--red)] text-white border-[var(--red)]" : "border-[var(--line-strong)] text-neutral-500 hover:text-black"}`}
    >
      <Pin className="h-3.5 w-3.5" />
    </button>
    <button
      type="button"
      onClick={() => onExclude(r)}
      aria-label={r.excluded ? "Réintégrer" : "Retirer"}
      title={r.excluded ? "Réintégrer" : "Retirer de cette édition"}
      data-testid={`nl-cand-exclude-${r.rid}`}
      className="p-1.5 rounded-sm border border-[var(--line-strong)] text-neutral-500 hover:text-black transition-colors"
    >
      {r.excluded ? <RotateCcw className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
    </button>
  </li>
);

export const NewsletterSelection = ({ onChange }) => {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/newsletter/candidates").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async (pinned, excluded) => {
    try {
      await api.put("/admin/newsletter/selection", { pinned, excluded });
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    }
  };
  if (!data) return null;
  const { pinned, excluded } = data.selection;
  const togglePin = (r) => save(pinned.includes(r.rid) ? pinned.filter((x) => x !== r.rid) : [...pinned, r.rid], excluded);
  const toggleExclude = (r) =>
    save(pinned.filter((x) => x !== r.rid), excluded.includes(r.rid) ? excluded.filter((x) => x !== r.rid) : [...excluded, r.rid]);

  const list = (title, items, testId) => (
    <div data-testid={testId}>
      <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--red)] mb-1">{title} · {items.length} sortie(s) cette semaine</div>
      {items.length === 0 ? (
        <div className="text-sm text-neutral-500 py-2">Aucune sortie avec affiche cette semaine.</div>
      ) : (
        <ul className="divide-y divide-[var(--line)] max-h-[420px] overflow-y-auto pr-1">
          {items.map((r) => <Row key={r.rid} r={r} onPin={togglePin} onExclude={toggleExclude} />)}
        </ul>
      )}
    </div>
  );

  return (
    <section data-testid="nl-selection-card" className="bg-white border border-[var(--line)] rounded-md p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl">Sélection de la prochaine édition</h2>
          <p className="text-xs text-neutral-500 mt-1">
            6 films et 6 séries, dont au moins {data.min_cinema} films en salle. Épinglez pour forcer un titre, retirez pour l'écarter.
            Remis à zéro après chaque envoi.
          </p>
        </div>
        {(pinned.length > 0 || excluded.length > 0) && (
          <button type="button" className="btn-ghost shrink-0" onClick={() => save([], [])} data-testid="nl-selection-reset">
            <RotateCcw className="h-4 w-4" /> Réinitialiser
          </button>
        )}
      </div>
      <div className="grid 2xl:grid-cols-2 gap-6">
        {list("Films", data.films, "nl-cand-films")}
        {list("Séries", data.series, "nl-cand-series")}
      </div>
    </section>
  );
};
